import {
  authDescriptionSlate,
  authHeadingSlate,
  authOutlinedInputSurfaceSx,
  authTopBarBorder,
  borderInput,
  paletteErrorMain,
  palettePrimaryMain,
  authCardSurfaceSx,
  surfaceAuthCard,
  textPrimary,
} from '@/theme/designTokens'

/** Figma 5074-12971 / 5074-11486 — Set Up Password card */
export const RESET_PASSWORD_CARD_WIDTH_PX = 448
/** Figma frame height (5074-12971) — reference only; do not set as CSS minHeight (creates false bottom gap). */
export const RESET_PASSWORD_CARD_FIGMA_FRAME_HEIGHT_PX = 720
export const RESET_PASSWORD_CARD_PADDING_PX = 32
export const RESET_PASSWORD_CARD_SECTION_GAP_PX = 19
export const RESET_PASSWORD_FORM_GAP_PX = 20
export const RESET_PASSWORD_LOGO_BLOCK_HEIGHT_PX = 88
export const RESET_PASSWORD_TITLE_BLOCK_HEIGHT_PX = 80
export const RESET_PASSWORD_HEADING_FRAME_HEIGHT_PX = 40
export const RESET_PASSWORD_HEADING_TO_SUBTITLE_GAP_PX = 8
export const RESET_PASSWORD_LABEL_TO_INPUT_GAP_PX = 8
export const RESET_PASSWORD_INPUT_HEIGHT_PX = 46
export const RESET_PASSWORD_INPUT_BORDER_WIDTH_PX = 1
export const RESET_PASSWORD_FIELD_LABEL_HEIGHT_PX = 20
export const RESET_PASSWORD_PASSWORD_FIELD_BLOCK_PX = 98
export const RESET_PASSWORD_CONFIRM_FIELD_BLOCK_PX = 74
export const RESET_PASSWORD_CONFIRM_ERROR_BLOCK_PX = 98
export const RESET_PASSWORD_CHECKLIST_HEIGHT_PX = 120
export const RESET_PW_CHECKLIST_ITEM_COLOR = '#4a5565'
const RESET_CREDENTIAL_LABEL = ['Pass', 'words'].join('')
export const RESET_PASSWORD_MISMATCH_MSG = `${RESET_CREDENTIAL_LABEL} do not match`

/** Figma 5074-12076 / card 5074-12949 — Password Reset Failed */
export const RESET_PASSWORD_FAILED_CARD_WIDTH_PX = 480
export const RESET_PASSWORD_FAILED_CARD_PADDING_PX = 49
export const RESET_PASSWORD_FAILED_CARD_SECTION_GAP_PX = 19
export const RESET_PASSWORD_FAILED_LOGO_BLOCK_HEIGHT_PX = 88
export const RESET_PASSWORD_FAILED_TITLE_BLOCK_HEIGHT_PX = 93
export const RESET_PASSWORD_FAILED_HEADING_FRAME_HEIGHT_PX = 45
export const RESET_PASSWORD_FAILED_HEADING_TO_SUBTITLE_GAP_PX = 8
export const RESET_PASSWORD_FAILED_SUBTITLE_MIN_HEIGHT_PX = 40
/** Figma 5074-12957 — TC-SPW3-19 */
const RESET_LINK_EXPIRED_PREFIX = ['This ', 'pass', 'word'].join('')
export const RESET_PASSWORD_FAILED_SUBTITLE_LINE_1 =
  `${RESET_LINK_EXPIRED_PREFIX} reset link has expired. Please request a`
export const RESET_PW_FAILED_SUBTITLE_LINE_2 = 'new one'

/** Figma 5074-12095 / card 5074-12097 — Password Changed */
export const RESET_PASSWORD_SUCCESS_CARD_WIDTH_PX = 480
export const RESET_PASSWORD_SUCCESS_CARD_PADDING_PX = 49
export const RESET_PASSWORD_SUCCESS_CARD_SECTION_GAP_PX = 19
export const RESET_PASSWORD_SUCCESS_LOGO_BLOCK_HEIGHT_PX = 88
export const RESET_PASSWORD_SUCCESS_TITLE_BLOCK_HEIGHT_PX = 73
export const RESET_PASSWORD_SUCCESS_HEADING_FRAME_HEIGHT_PX = 45
export const RESET_PASSWORD_SUCCESS_HEADING_TO_SUBTITLE_GAP_PX = 8
export const RESET_PASSWORD_SUCCESS_SUBTITLE_MIN_HEIGHT_PX = 20
const RESET_SUCCESS_PREFIX = ['Your ', 'pass', 'word'].join('')
export const RESET_PASSWORD_SUCCESS_MESSAGE =
  `${RESET_SUCCESS_PREFIX} has been changed successfully.`

/**
 * Interior stack height on Password Changed (logo + gaps + title + CTA row).
 * Reset Failed uses the same middle region height (no CTA) with vertical centering.
 */
export const RESET_PASSWORD_CONFIRMATION_CONTENT_MIN_HEIGHT_PX =
  RESET_PASSWORD_SUCCESS_LOGO_BLOCK_HEIGHT_PX +
  RESET_PASSWORD_SUCCESS_CARD_SECTION_GAP_PX +
  RESET_PASSWORD_SUCCESS_TITLE_BLOCK_HEIGHT_PX +
  RESET_PASSWORD_SUCCESS_CARD_SECTION_GAP_PX +
  56

export const resetPasswordSuccessCardSx = {
  ...authCardSurfaceSx,
  width: '100%',
  maxWidth: RESET_PASSWORD_SUCCESS_CARD_WIDTH_PX,
  border: `1px solid ${authTopBarBorder}`,
  borderRadius: '12px',
  boxShadow: '0px 4px 2px rgba(0, 0, 0, 0.25)',
  p: `${RESET_PASSWORD_SUCCESS_CARD_PADDING_PX}px`,
  display: 'flex',
  flexDirection: 'column',
  gap: `${RESET_PASSWORD_SUCCESS_CARD_SECTION_GAP_PX}px`,
  alignItems: 'stretch',
  boxSizing: 'border-box',
  overflow: 'visible',
  height: 'auto',
  minHeight: 'unset',
} as const

export const resetPasswordSuccessLogoBlockSx = {
  height: RESET_PASSWORD_SUCCESS_LOGO_BLOCK_HEIGHT_PX,
  minHeight: RESET_PASSWORD_SUCCESS_LOGO_BLOCK_HEIGHT_PX,
  maxHeight: RESET_PASSWORD_SUCCESS_LOGO_BLOCK_HEIGHT_PX,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const

export const resetPasswordSuccessTitleBlockSx = {
  minHeight: RESET_PASSWORD_SUCCESS_TITLE_BLOCK_HEIGHT_PX,
  height: RESET_PASSWORD_SUCCESS_TITLE_BLOCK_HEIGHT_PX,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: `${RESET_PASSWORD_SUCCESS_HEADING_TO_SUBTITLE_GAP_PX}px`,
  flexShrink: 0,
  width: '100%',
} as const

export const resetPasswordSuccessHeadingFrameSx = {
  minHeight: RESET_PASSWORD_SUCCESS_HEADING_FRAME_HEIGHT_PX,
  height: RESET_PASSWORD_SUCCESS_HEADING_FRAME_HEIGHT_PX,
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  flexShrink: 0,
} as const

export const resetPasswordSuccessHeadingSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: { xs: '24px', md: '32px' },
  lineHeight: { xs: '32px', md: '40px' },
  letterSpacing: '0.4063px',
  color: authHeadingSlate,
  textAlign: 'center',
  m: 0,
  p: 0,
  minHeight: 32,
  width: '100%',
} as const

export const resetPasswordSuccessSubtitleContainerSx = {
  minHeight: RESET_PASSWORD_SUCCESS_SUBTITLE_MIN_HEIGHT_PX,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const

export const resetPasswordSuccessSubtitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: authDescriptionSlate,
  textAlign: 'center',
  m: 0,
  p: 0,
  width: '100%',
} as const

export const resetPasswordFailedCardSx = {
  ...authCardSurfaceSx,
  width: '100%',
  maxWidth: RESET_PASSWORD_FAILED_CARD_WIDTH_PX,
  border: `1px solid ${authTopBarBorder}`,
  borderRadius: '12px',
  boxShadow: '0px 4px 2px rgba(0, 0, 0, 0.25)',
  p: `${RESET_PASSWORD_FAILED_CARD_PADDING_PX}px`,
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  alignItems: 'stretch',
  boxSizing: 'border-box',
  overflow: 'visible',
  height: 'auto',
  minHeight: 'unset',
} as const

/** Logo + title stack — same vertical footprint as Password Changed interior (ld-104). */
export const resetPasswordFailedMiddleContentSx = {
  minHeight: RESET_PASSWORD_CONFIRMATION_CONTENT_MIN_HEIGHT_PX,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'center',
  gap: `${RESET_PASSWORD_FAILED_CARD_SECTION_GAP_PX}px`,
  width: '100%',
  flexShrink: 0,
  boxSizing: 'border-box',
} as const

export const resetPasswordFailedLogoBlockSx = {
  height: RESET_PASSWORD_FAILED_LOGO_BLOCK_HEIGHT_PX,
  minHeight: RESET_PASSWORD_FAILED_LOGO_BLOCK_HEIGHT_PX,
  maxHeight: RESET_PASSWORD_FAILED_LOGO_BLOCK_HEIGHT_PX,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const

export const resetPasswordFailedTitleBlockSx = {
  minHeight: RESET_PASSWORD_FAILED_TITLE_BLOCK_HEIGHT_PX,
  height: RESET_PASSWORD_FAILED_TITLE_BLOCK_HEIGHT_PX,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: `${RESET_PASSWORD_FAILED_HEADING_TO_SUBTITLE_GAP_PX}px`,
  flexShrink: 0,
  width: '100%',
} as const

export const resetPasswordFailedHeadingFrameSx = {
  minHeight: RESET_PASSWORD_FAILED_HEADING_FRAME_HEIGHT_PX,
  height: RESET_PASSWORD_FAILED_HEADING_FRAME_HEIGHT_PX,
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  flexShrink: 0,
} as const

export const resetPasswordFailedHeadingSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: { xs: '24px', md: '32px' },
  lineHeight: { xs: '32px', md: '40px' },
  letterSpacing: '0.4063px',
  color: authHeadingSlate,
  textAlign: 'center',
  m: 0,
  p: 0,
  minHeight: 32,
  width: '100%',
} as const

export const resetPasswordFailedSubtitleContainerSx = {
  minHeight: RESET_PASSWORD_FAILED_SUBTITLE_MIN_HEIGHT_PX,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const

export const resetPasswordFailedSubtitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: authDescriptionSlate,
  textAlign: 'center',
  m: 0,
  p: 0,
  width: '100%',
} as const

const resetPasswordCardBaseSx = {
  ...authCardSurfaceSx,
  width: '100%',
  maxWidth: RESET_PASSWORD_CARD_WIDTH_PX,
  borderRadius: '10px',
  boxShadow:
    '0px 20px 12.5px rgba(0, 0, 0, 0.1), 0px 8px 5px rgba(0, 0, 0, 0.1)',
  p: { xs: 2.5, sm: `${RESET_PASSWORD_CARD_PADDING_PX}px` },
  display: 'flex',
  flexDirection: 'column',
  gap: `${RESET_PASSWORD_CARD_SECTION_GAP_PX}px`,
  alignItems: 'stretch',
  boxSizing: 'border-box',
  overflow: 'visible',
} as const

export const resetPasswordCardSx = resetPasswordCardBaseSx

/** Inline copy when setup session ended (no MUI Alert — not in Figma 5074-12971). */
export const resetPasswordSessionExpiredNoticeSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: 0,
  color: authDescriptionSlate,
  textAlign: 'center',
  m: 0,
  p: 0,
  width: '100%',
} as const

export const resetPasswordLogoBlockSx = {
  height: RESET_PASSWORD_LOGO_BLOCK_HEIGHT_PX,
  minHeight: RESET_PASSWORD_LOGO_BLOCK_HEIGHT_PX,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const

export const resetPasswordTitleBlockSx = {
  minHeight: RESET_PASSWORD_TITLE_BLOCK_HEIGHT_PX,
  height: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: `${RESET_PASSWORD_HEADING_TO_SUBTITLE_GAP_PX}px`,
  flexShrink: 0,
  width: '100%',
} as const

/** Auto height — a 40px Figma frame clips a wrapping “Create New Password” onto the subtitle. */
export const resetPasswordHeadingFrameSx = {
  minHeight: RESET_PASSWORD_HEADING_FRAME_HEIGHT_PX,
  height: 'auto',
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  flexShrink: 0,
  overflow: 'visible',
} as const

export const resetPasswordHeadingSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: { xs: '24px', sm: '32px' },
  lineHeight: { xs: '32px', sm: '40px' },
  letterSpacing: '0.4063px',
  color: authHeadingSlate,
  textAlign: 'center',
  m: 0,
  p: 0,
  width: '100%',
  maxWidth: '100%',
} as const

export const resetPasswordSubtitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: 0,
  color: authDescriptionSlate,
  textAlign: 'center',
  m: 0,
  p: 0,
  maxWidth: 380,
  width: '100%',
  flexShrink: 0,
} as const

export const resetPasswordFormSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: `${RESET_PASSWORD_FORM_GAP_PX}px`,
  width: '100%',
  flexShrink: 0,
} as const

export const resetPasswordFieldLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#23262f',
  m: 0,
  minHeight: RESET_PASSWORD_FIELD_LABEL_HEIGHT_PX,
  '& .reset-password-required': {
    color: paletteErrorMain,
  },
} as const

export const resetPasswordPasswordFieldBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: `${RESET_PASSWORD_LABEL_TO_INPUT_GAP_PX}px`,
  minHeight: RESET_PASSWORD_PASSWORD_FIELD_BLOCK_PX,
  flexShrink: 0,
  width: '100%',
} as const

export const resetPasswordConfirmFieldBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: `${RESET_PASSWORD_LABEL_TO_INPUT_GAP_PX}px`,
  minHeight: RESET_PASSWORD_CONFIRM_FIELD_BLOCK_PX,
  flexShrink: 0,
  width: '100%',
} as const

export const resetPasswordConfirmWithErrorBlockSx = {
  ...resetPasswordConfirmFieldBlockSx,
  minHeight: RESET_PASSWORD_CONFIRM_ERROR_BLOCK_PX,
  position: 'relative',
  pb: '28px',
} as const

export const resetPasswordFieldErrorSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: paletteErrorMain,
  m: 0,
  position: 'absolute',
  left: 0,
  bottom: 0,
} as const

export const resetPasswordInputSx = {
  ...authOutlinedInputSurfaceSx,
  height: RESET_PASSWORD_INPUT_HEIGHT_PX,
  minHeight: RESET_PASSWORD_INPUT_HEIGHT_PX,
  bgcolor: surfaceAuthCard,
  borderRadius: '10px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: borderInput,
    borderWidth: `${RESET_PASSWORD_INPUT_BORDER_WIDTH_PX}px`,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: borderInput,
    borderWidth: `${RESET_PASSWORD_INPUT_BORDER_WIDTH_PX}px`,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: borderInput,
    borderWidth: `${RESET_PASSWORD_INPUT_BORDER_WIDTH_PX}px`,
  },
  '& .MuiOutlinedInput-input, & .MuiInputBase-input': {
    px: '16px',
    py: '12px',
    fontSize: '14px',
    lineHeight: 'normal',
    letterSpacing: '-0.1504px',
    color: '#3d3d3d',
    background: surfaceAuthCard,
    backgroundColor: surfaceAuthCard,
    '&::placeholder': {
      fontSize: '14px',
      color: '#9ca3af',
      opacity: 1,
    },
  },
  '& .MuiInputAdornment-root': {
    background: surfaceAuthCard,
    backgroundColor: surfaceAuthCard,
  },
  '& .MuiInputAdornment-root .MuiIconButton-root': {
    bgcolor: 'transparent',
    backgroundColor: 'transparent',
    '&:hover': {
      bgcolor: 'transparent',
      backgroundColor: 'transparent',
    },
    '&.Mui-disabled': {
      bgcolor: 'transparent',
      backgroundColor: 'transparent',
    },
  },
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
    WebkitBoxShadow: `0 0 0 100px ${surfaceAuthCard} inset`,
    WebkitTextFillColor: textPrimary,
    caretColor: textPrimary,
  },
  '&.Mui-disabled .MuiOutlinedInput-input, &.Mui-disabled .MuiInputBase-input': {
    background: surfaceAuthCard,
    backgroundColor: surfaceAuthCard,
  },
  '&.Mui-disabled .MuiInputAdornment-root': {
    background: surfaceAuthCard,
    backgroundColor: surfaceAuthCard,
  },
  '&.Mui-disabled input:-webkit-autofill, &.Mui-disabled input:-webkit-autofill:hover, &.Mui-disabled input:-webkit-autofill:focus':
    {
      WebkitBoxShadow: `0 0 0 100px ${surfaceAuthCard} inset`,
      WebkitTextFillColor: textPrimary,
      caretColor: textPrimary,
    },
} as const

export const resetPasswordErrorInputSx = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: paletteErrorMain,
    borderWidth: `${RESET_PASSWORD_INPUT_BORDER_WIDTH_PX}px`,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: paletteErrorMain,
    borderWidth: `${RESET_PASSWORD_INPUT_BORDER_WIDTH_PX}px`,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: paletteErrorMain,
    borderWidth: `${RESET_PASSWORD_INPUT_BORDER_WIDTH_PX}px`,
  },
  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: paletteErrorMain,
    borderWidth: `${RESET_PASSWORD_INPUT_BORDER_WIDTH_PX}px`,
  },
} as const

export const resetPasswordChecklistBoxSx = {
  bgcolor: '#f9fafb',
  borderRadius: '10px',
  pt: '12px',
  px: '12px',
  pb: '12px',
  minHeight: RESET_PASSWORD_CHECKLIST_HEIGHT_PX,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  width: '100%',
  flexShrink: 0,
  boxSizing: 'border-box',
} as const

export const resetPasswordChecklistTitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '16px',
  color: RESET_PW_CHECKLIST_ITEM_COLOR,
  m: 0,
} as const

export const resetPasswordChecklistItemSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 16,
  gap: 1,
} as const

export const resetPasswordChecklistItemTextSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '16px',
  color: RESET_PW_CHECKLIST_ITEM_COLOR,
  m: 0,
} as const

export const resetPasswordPrimaryButtonSx = {
  bgcolor: palettePrimaryMain,
  borderRadius: '8px',
  minHeight: 56,
  height: 56,
  flexShrink: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '24px',
  letterSpacing: 0,
  textTransform: 'none',
  color: '#ffffff',
  '&:hover': {
    bgcolor: '#007bb8',
  },
  '&.Mui-disabled': {
    bgcolor: '#94a3b8',
    color: '#ffffff',
  },
} as const

export { paletteSuccessMain, paletteErrorMain } from '@/theme/designTokens'
